/**
 * CREATOR ATTRIBUTION — "Created by Goodluck with Lyrixa AI".
 *
 * ============================================================================
 * NO NAME IS HARDCODED IN THIS FILE, AND THAT IS ITS ENTIRE REASON FOR EXISTING.
 *
 * Batch 7 §17: "Do not hardcode creator names." Every string returned here is derived
 * from the `Song` record's own `creatorName`, which the backend resolves from the
 * account that owns the song. Nothing in this module knows a person's name, and
 * `grep "Created by"` finds one template — here — rather than a name in a component.
 * ============================================================================
 *
 * WHY A LIBRARY AND NOT A LINE IN THE CARD. Three surfaces show a catalogue song today
 * (the featured hero, the trending rows, the explore grid) and a fourth shows one song
 * on its own (the share page). Attribution written inline would be four templates that
 * agree until one of them is edited — and the one thing worse than no attribution is
 * two surfaces crediting the same track differently.
 *
 * FIRST NAME ONLY, WHICH IS A PRIVACY DECISION AND NOT A STYLE ONE
 * ---------------------------------------------------------------
 * §17 asks for "Created by [First Name] with Lyrixa AI", and the reason to honour the
 * bracket exactly is that the public catalogue is public. A user who signed up as
 * "Goodluck Efe" to get an account has not thereby asked for their full legal name to
 * appear beside a track that strangers can browse. A first name identifies the creator
 * to people who know them and does not publish a surname to people who do not.
 *
 * So `firstNameOf` takes the first whitespace-separated word and the record keeps the
 * whole string. The trimming is here, at render time, rather than in the backend
 * response: the full name is legitimately needed elsewhere (the account's own header,
 * the license certificate), so a backend that sent only a first name would break those,
 * and a frontend that stored only a first name could never show a full one.
 *
 * WHAT ABSENT MEANS, AND WHY NOTHING IS INVENTED
 * ---------------------------------------------
 * `creatorName` is optional on `Song`, and every function here returns `null` when it is
 * missing rather than a fallback. There is no "Unknown creator", no "A Lyrixa creator",
 * and no substitution of the signed-in user's own name.
 *
 * The last of those is the tempting one and it is the actual bug this file prevents. The
 * signed-in user is available from `authStore` at every one of these call sites, and
 * "the current user made it" is true for every song in this build — so falling back to
 * it would look correct today and would attribute other people's music to whoever is
 * looking the moment a real catalogue exists. Attribution is not a field to guess at.
 *
 * The visible consequence: the four seed songs carry no creator, so no attribution line
 * renders on them. That is correct — they stand in for records a real user owns, and
 * nothing in the fixture says who. See data/songs.ts, which refuses the same invention
 * for `visibility`.
 */
import type { Song, User } from '../types/domain';

/**
 * The product name, as it appears in the attribution line.
 *
 * A constant so the one place it is written is the one place it is changed, and so a
 * test asserts the same literal the UI renders. "Lyrixa AI" — the product's name — not
 * "Lyrixa", which is the company, and not the model name, which is `Song.aiModel` and a
 * different fact.
 */
export const PRODUCT_NAME = 'Lyrixsa AI';

/**
 * The first name in a display name, or null when there isn't one.
 *
 * Splits on any whitespace run rather than a single space, so a pasted name with a
 * double space or a tab does not yield an empty first word.
 *
 * NOT A NAME PARSER, and deliberately not trying to be. It does not know about
 * honorifics, particles ("van der"), given-name-last conventions, or mononyms beyond
 * handling them correctly by accident (a single word is returned whole, which is right).
 * Splitting on whitespace is a heuristic and it is the honest ceiling for a field that
 * is one free-text string; the alternative is asking every user to enter given and
 * family names separately, which is a signup-form decision and not this file's to make.
 *
 * Returns null for an empty or whitespace-only string, so a record with `creatorName:
 * ''` — which a backend could send — renders no attribution rather than "Created by
 * with Lyrixa AI".
 */
export function firstNameOf(fullName: string | undefined | null): string | null {
  if (!fullName) return null;
  const [first] = fullName.trim().split(/\s+/);
  return first ? first : null;
}

/**
 * The creator's first name for a song, or null when the song records no creator.
 *
 * The one accessor every attribution surface goes through, so "which field holds the
 * creator" is answered in one place. If `Song` later grows a separate `creatorFirstName`
 * from the backend, this is the only function that changes.
 */
export function creatorNameFor(song: Song): string | null {
  return firstNameOf(song.creatorName);
}

/**
 * THE TEMPLATE. Private, and the only place the long form's words are written.
 *
 * Three exported functions below compose this line from three different starting points (a
 * song record, a public share payload, the signed-in user) and every one of them differs
 * ONLY in where the name came from — never in what the sentence says. Written once here so
 * that remains true: three copies of `Created by ${x} with ${PRODUCT_NAME}` would be three
 * things to edit and two chances to leave one behind.
 *
 * Takes a first name that has already been resolved, so the null-handling is in one place
 * too: an absent name yields an absent line at every call site rather than at three.
 */
function attributionLine(firstName: string | null): string | null {
  return firstName ? `Created by ${firstName} with ${PRODUCT_NAME}` : null;
}

function uploadedAttributionLine(name: string | null): string | null {
  return name ? `Uploaded by ${name} with ${PRODUCT_NAME}` : null;
}

/**
 * The attribution line for a song, or null when there is no creator to credit.
 *
 * "Created by Goodluck with Lyrixa AI" — §17's form, with the first name substituted
 * from the record.
 *
 * RETURNS NULL RATHER THAN A PARTIAL LINE. A surface receiving null renders nothing:
 * no element, no empty space, no "Created with Lyrixa AI" with the person dropped out.
 * That last one is the subtle wrong answer — it is a true sentence about the product
 * that quietly replaces a statement about a person, so a reader cannot tell an
 * unattributed song from an unattributable one.
 */
export function attributionFor(song: Song): string | null {
  return song.aiModel === 'Uploaded audio'
    ? uploadedAttributionLine(firstNameOf(song.uploaderName ?? song.creatorName))
    : attributionLine(creatorNameFor(song));
}

/**
 * The attribution line for a creator name that did not arrive on a `Song`.
 *
 * FOR THE PUBLIC SHARE PAGE, which is the one attribution surface that does not hold a song
 * record. `GET /share/:slug` returns `SharedSong` — deliberately narrower than `Song`, with
 * the prompt, the credits and the version history stripped out — so `attributionFor` cannot
 * be handed one, and the alternative was widening the public payload into a full `Song` to
 * satisfy a type. That would have been the wrong direction entirely.
 *
 * IT TAKES A NAME AND NOT AN ACCOUNT, which is what keeps it as safe as `attributionFor`.
 * The caller must already hold a creator name that the backend put on the record it is
 * displaying; there is no id to resolve, no store to read, and nothing here that could
 * reach for the signed-in user. On the share page there is no signed-in user to reach for
 * — the recipient is a stranger — and that is exactly the surface where a fallback would
 * have been most wrong.
 *
 * Same trimming, same null, same sentence as `attributionFor`. See `attributionLine`.
 */
export function attributionForName(fullName: string | undefined | null): string | null {
  return attributionLine(firstNameOf(fullName));
}

/**
 * The short attribution line — "Created by Goodluck", with no product name.
 *
 * §17 offers both forms ("Created by [First Name]" or "… with Lyrixa AI") and this is the
 * first one, for a surface too dense to carry the second. The Trending chart is the case:
 * its rows are 44px thumbnails with a title and one metadata line, and appending "with
 * Lyrixa AI" there spends a third of a narrow row restating the name of the app the reader
 * is already inside. Every song in that list was made with Lyrixa AI; the fact worth the
 * width is who made it.
 *
 * HERE RATHER THAN IN THE COMPONENT, which is the point of this module: two forms in one
 * file that share `creatorNameFor` stay in step, while a component writing `Created by
 * ${name}` inline is a second template and the start of two surfaces disagreeing. `grep
 * "Created by"` still finds exactly this file.
 *
 * Returns null on a song with no creator, for `attributionFor`'s reasons — no fallback, no
 * "Unknown creator", and never the signed-in user.
 */
export function shortAttributionFor(song: Song): string | null {
  const name = creatorNameFor(song);
  if (song.aiModel === 'Uploaded audio') {
    const uploader = firstNameOf(song.uploaderName ?? song.creatorName);
    return uploader ? `Uploaded by ${uploader}` : null;
  }
  return name ? `Created by ${name}` : null;
}

/**
 * The attribution line for the signed-in user's own song, for a surface that is
 * previewing how their track will be credited before it has a creator on the record.
 *
 * WHY THIS IS A SEPARATE FUNCTION AND NOT A FALLBACK INSIDE `attributionFor`. The
 * distinction is the whole safety property of this module: `attributionFor` reads the
 * record and cannot be tricked into crediting the wrong person, while this one is
 * explicitly "credit THIS user", and a caller has to say so. Making it a fallback would
 * mean every catalogue tile silently credited the viewer for songs they did not make.
 *
 * The one legitimate caller is the Make Discoverable dialog, which is showing the owner
 * what the catalogue will say about their own song at the moment they decide to publish
 * it — the account is unambiguous there, because they are the one clicking.
 *
 * Returns null for a signed-out user or a nameless one, for `attributionFor`'s reason.
 */
export function attributionForUser(user: User | null): string | null {
  return attributionLine(firstNameOf(user?.name));
}
